/**
 * Global background music and one-shot UI sounds. Wraps the router (inside AuthProvider) so it
 * can read `user.settings.music` and `user.settings.sfx` as 0–100 levels (50 ≈ 50% element
 * volume). BGM uses one HTMLAudioElement and cycles `BGM_PLAYLIST` on `ended`. SFX use a small
 * pool so rapid hovers do not cut off the same sample. Playback starts only after a user gesture
 * (autoplay policy). Level 0 mutes that bus; unset settings default to 100.
 */
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    type ReactNode,
} from 'react';

import { useAuth } from '../AuthProvider';
import hoverSfxUrl from '../assets/audio/hover1.ogg';
import { BGM_PLAYLIST } from './bgmPlaylist';

const HOVER_SFX_POOL_SIZE = 3;
const DEFAULT_SETTING_LEVEL = 100;

/** Map settings 0–100 to HTMLAudioElement.volume 0–1. */
function settingLevelToVolume01(level: number): number {
    const clamped = Math.min(100, Math.max(0, level));
    return clamped / 100;
}

/**
 * Coerce stored setting to 0–100 (handles legacy booleans in cached user JSON).
 */
function normalizeSettingLevel0to100(value: unknown): number {
    if (typeof value === 'boolean') {
        return value ? DEFAULT_SETTING_LEVEL : 0;
    }
    const n = Math.round(Number(value));
    if (!Number.isFinite(n)) {
        return DEFAULT_SETTING_LEVEL;
    }
    return Math.min(100, Math.max(0, n));
}

export type AudioContextValue = {
    playerHover: () => void;
};

const AudioContext = createContext<AudioContextValue | null>(null);

export function useAudio(): AudioContextValue {
    const ctx = useContext(AudioContext);
    if (!ctx) {
        throw new Error('useAudio must be used within AudioProvider');
    }
    return ctx;
}

const BGM_LOG = '[BGM]';

function logBgmMediaError(audio: HTMLAudioElement, context: string): void {
    const err = audio.error;
    if (!err) {
        console.error(BGM_LOG, context, 'audio.error is null', audio.src);
        return;
    }
    const codeNames: Record<number, string> = {
        1: 'MEDIA_ERR_ABORTED',
        2: 'MEDIA_ERR_NETWORK',
        3: 'MEDIA_ERR_DECODE',
        4: 'MEDIA_ERR_SRC_NOT_SUPPORTED',
    };
    console.error(
        BGM_LOG,
        context,
        codeNames[err.code] ?? `code_${err.code}`,
        err.message || '(no message)',
        audio.src
    );
}

function logBgmPlayRejected(err: unknown, audio: HTMLAudioElement, context: string): void {
    const msg = err instanceof Error ? err.message : String(err);
    console.warn(BGM_LOG, 'play() rejected', context, msg, {
        src: audio.src,
        currentTime: audio.currentTime,
        readyState: audio.readyState,
    });
}

export const AudioProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { user } = useAuth();
    const musicLevel = normalizeSettingLevel0to100(
        user.settings?.music ?? DEFAULT_SETTING_LEVEL
    );
    const sfxLevel = normalizeSettingLevel0to100(user.settings?.sfx ?? DEFAULT_SETTING_LEVEL);

    const unlockedRef = useRef(false);
    const trackIndexRef = useRef(0);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const musicLevelRef = useRef(musicLevel);
    const sfxLevelRef = useRef(sfxLevel);

    musicLevelRef.current = musicLevel;
    sfxLevelRef.current = sfxLevel;

    const syncPlaybackRef = useRef<() => void>(() => {});
    const hoverSfxPoolRef = useRef<HTMLAudioElement[] | null>(null);
    const hoverSfxRoundRobinRef = useRef(0);

    // One-time: create audio element, advance playlist on track end (modulo = loop to first).
    if (!audioRef.current) {
        const audio = new Audio(BGM_PLAYLIST[0]);
        audio.preload = 'auto';
        audio.volume = settingLevelToVolume01(musicLevelRef.current);

        audio.addEventListener('error', () => {
            logBgmMediaError(audio, 'Media failed to load or decode');
        });

        // Next track in playlist; index wraps so last song goes back to the first.
        const advance = () => {
            const prevIndex = trackIndexRef.current;
            trackIndexRef.current = (trackIndexRef.current + 1) % BGM_PLAYLIST.length;
            console.info(BGM_LOG, 'track ended → advancing', {
                fromIndex: prevIndex,
                toIndex: trackIndexRef.current,
                nextSrc: BGM_PLAYLIST[trackIndexRef.current],
            });
            audio.src = BGM_PLAYLIST[trackIndexRef.current];
            audio.volume = settingLevelToVolume01(musicLevelRef.current);
            if (musicLevelRef.current > 0 && unlockedRef.current) {
                void audio.play().catch((err) => logBgmPlayRejected(err, audio, 'after advance'));
            }
        };

        audio.addEventListener('ended', advance);
        audioRef.current = audio;
    }

    if (!hoverSfxPoolRef.current) {
        const pool: HTMLAudioElement[] = [];
        for (let i = 0; i < HOVER_SFX_POOL_SIZE; i++) {
            const sfx = new Audio(hoverSfxUrl);
            sfx.preload = 'auto';
            sfx.volume = settingLevelToVolume01(sfxLevelRef.current);
            pool.push(sfx);
        }
        hoverSfxPoolRef.current = pool;
    }

    const playerHover = useCallback(() => {
        if (!unlockedRef.current || sfxLevelRef.current <= 0) return;
        const pool = hoverSfxPoolRef.current;
        if (!pool?.length) return;

        const idx = hoverSfxRoundRobinRef.current % pool.length;
        hoverSfxRoundRobinRef.current++;
        const el = pool[idx];
        el.currentTime = 0;
        void el.play().catch(() => {});
    }, []);

    const audioContextValue = useMemo(() => ({ playerHover }), [playerHover]);

    // Pause if music muted (0); otherwise play when unlocked (no-op until first gesture).
    const syncPlayback = () => {
        const audio = audioRef.current;
        if (!audio) return;

        audio.volume = settingLevelToVolume01(musicLevelRef.current);

        if (musicLevelRef.current <= 0) {
            audio.pause();
            return;
        }

        if (!unlockedRef.current) {
            return;
        }

        void audio.play().catch((err) => logBgmPlayRejected(err, audio, 'syncPlayback'));
    };

    syncPlaybackRef.current = syncPlayback;

    // First user interaction unlocks audio context so play() can succeed.
    const unlockAndMaybePlay = () => {
        if (unlockedRef.current) return;
        unlockedRef.current = true;
        syncPlaybackRef.current();
    };

    // Attach gesture listeners once for autoplay unlock.
    useEffect(() => {
        const onPointer = () => unlockAndMaybePlay();
        const onKeyDown = () => unlockAndMaybePlay();

        window.addEventListener('pointerdown', onPointer, { passive: true });
        window.addEventListener('keydown', onKeyDown);
        window.addEventListener('touchstart', onPointer, { passive: true });

        return () => {
            window.removeEventListener('pointerdown', onPointer);
            window.removeEventListener('keydown', onKeyDown);
            window.removeEventListener('touchstart', onPointer);
        };
    }, []);

    // React when the user changes music level (volume or mute).
    useEffect(() => {
        syncPlayback();
    }, [musicLevel]);

    // Apply SFX bus volume; pause hover samples when level is 0.
    useEffect(() => {
        const pool = hoverSfxPoolRef.current;
        if (!pool) return;
        const v = settingLevelToVolume01(sfxLevel);
        for (const el of pool) {
            el.volume = v;
        }
        if (sfxLevel > 0) return;
        for (const el of pool) {
            el.pause();
            el.currentTime = 0;
        }
    }, [sfxLevel]);

    // Stop audio on unmount (e.g. full page navigation in tests or HMR).
    useEffect(() => {
        return () => {
            const audio = audioRef.current;
            if (!audio) return;
            audio.pause();
            audio.currentTime = 0;

            const pool = hoverSfxPoolRef.current;
            if (pool) {
                for (const sfx of pool) {
                    sfx.pause();
                    sfx.currentTime = 0;
                }
            }
        };
    }, []);

    return <AudioContext.Provider value={audioContextValue}>{children}</AudioContext.Provider>;
};
